import {getApp} from "firebase/app";
import {httpsCallable, getFunctions} from "firebase/functions";
import {collection, doc, DocumentData, Timestamp} from "firebase/firestore";
import {appFirestore} from "../../index";
import {RREventType} from "../types/RREventType";

type WireData = Record<string, string | number | boolean | null>;
type ScheduleRequest = {action: "event" | "calendar"; id: string; requestId: string; create?: boolean; revision?: number; values: WireData};
const pendingIds = new Map<string, string>();
const createIds = new Map<string, string>();
const calendarFields = ["dayType", "eventId", "employeeId", "employeeName", "eventDayStart", "eventDayEnd", "description", "isWarehouse", "isMontage", "isSupervisor", "isHoliday", "face", "notes", "department", "restTime", "bonus", "isConfirmed"];
const eventFields = ["eventNumber", "eventName", "eventShortName", "eventStatus", "eventColor", "eventCategory", "place", "clientEmail", "clientName", "clientPhone", "clientSpoc", "firstDate", "lastDate", "face", "notes", "isDeleted", "isCatering", "isStore", "isMontage"];

/** Не изпраща DocumentReference, UI идентификатори или други непознати полета към сървъра. */
function wireData(data: DocumentData, allowed: string[]): WireData {
    const result: WireData = {};
    for (const key of allowed) {
        const value = data[key];
        if (value === undefined) continue;
        if (value instanceof Timestamp) result[key] = value.toDate().toISOString();
        else if (value instanceof Date) result[key] = value.toISOString();
        else if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") result[key] = value;
        else throw new Error("Невалидна стойност във формата.");
    }
    return result;
}

async function mutate(request: Omit<ScheduleRequest, "requestId">): Promise<void> {
    const fingerprint = JSON.stringify(request);
    const requestId = pendingIds.get(fingerprint) || doc(collection(appFirestore, "eventScheduleRequests")).id;
    pendingIds.set(fingerprint, requestId);
    const callable = httpsCallable<ScheduleRequest, {id: string}>(getFunctions(getApp(), "europe-west1"), "saveEventSchedule");
    await callable({...request, requestId});
    pendingIds.delete(fingerprint);
}

export async function createCalendarPlan(data: DocumentData): Promise<void> {
    const values = wireData(data, calendarFields);
    const key = JSON.stringify(values);
    const id = createIds.get(key) || doc(collection(appFirestore, "calendar")).id;
    createIds.set(key, id);
    await mutate({action: "calendar", id, create: true, values});
    createIds.delete(key);
}

export async function updateCalendarPlan(id: string, data: DocumentData): Promise<void> {
    await mutate({action: "calendar", id, values: wireData(data, calendarFields)});
}

export async function saveScheduledEvent(event: RREventType, create: boolean): Promise<void> {
    const values = wireData(event, eventFields);
    const key = `event:${JSON.stringify(values)}`;
    const id = event.id || createIds.get(key) || doc(collection(appFirestore, "events")).id;
    if (create) createIds.set(key, id);
    await mutate({action: "event", id, create, revision: event.planningRevision || 0, values});
    if (create) createIds.delete(key);
}
